import * as fs from 'fs-extra';
import * as path from 'path';
import { ClaudeMigrationInstruction, ClaudeValidationRule } from './claude-migration-engine';

export interface MigrationTemplateConfig {
  fromVersion: string;
  toVersion: string;
  changes: VersionChangeSet;
}

export interface VersionChangeSet {
  dependencies: {
    add: Array<{ name: string; version: string; dev?: boolean }>;
    remove: string[];
    update: Array<{ name: string; from: string; to: string }>;
  };
  breakingChanges: {
    removedApis: string[];
    changedApis: Array<{ old: string; new: string; reason: string }>;
    newRequirements: string[];
  };
  newFeatures: {
    framework?: 'tailwind' | 'styled-components' | 'emotion' | 'css-modules';
    designTokens?: boolean;
    typescript?: boolean;
    testing?: 'jest' | 'vitest' | 'cypress';
    linting?: 'eslint' | 'biome';
    formatting?: 'prettier' | 'biome';
  };
  codeTransforms: Array<{
    name: string;
    description: string;
    filePatterns: string[];
    complexity: 'low' | 'medium' | 'high';
  }>;
}

/**
 * Generates Claude migration instructions for version upgrades
 */
export class MigrationInstructionGenerator {
  private templatesDir: string;

  constructor(templatesDir?: string) {
    this.templatesDir = templatesDir || path.join(__dirname, 'migration-templates');
  }

  /**
   * Generate migration instructions for a version upgrade
   */
  async generateInstructions(config: MigrationTemplateConfig): Promise<ClaudeMigrationInstruction[]> {
    const instructions: ClaudeMigrationInstruction[] = [];

    // Generate dependency migration
    if (this.hasDependencyChanges(config.changes)) {
      instructions.push(this.generateDependencyMigration(config));
    }

    // Generate framework-specific migrations
    if (config.changes.newFeatures.framework === 'tailwind') {
      instructions.push(await this.generateTailwindMigration(config));
    }

    // Generate code transform migrations
    for (const transform of config.changes.codeTransforms) {
      instructions.push(await this.generateCodeTransformMigration(config, transform));
    }

    // Generate TypeScript migration if needed
    if (config.changes.newFeatures.typescript) {
      instructions.push(await this.generateTypeScriptMigration(config));
    }

    // Generate testing framework migration
    if (config.changes.newFeatures.testing) {
      instructions.push(await this.generateTestingMigration(config));
    }

    return instructions;
  }

  /**
   * Save generated instructions to disk
   */
  async saveInstructions(
    instructions: ClaudeMigrationInstruction[], 
    outputDir: string
  ): Promise<void> {
    await fs.ensureDir(outputDir);

    for (const instruction of instructions) {
      const filename = `${instruction.id}.json`;
      const filepath = path.join(outputDir, filename);
      await fs.writeJson(filepath, instruction, { spaces: 2 });
    }

    // Generate migration index
    const index = {
      version: instructions[0]?.version,
      instructions: instructions.map(i => ({
        id: i.id,
        title: i.title,
        description: i.description,
        filePatterns: i.filePatterns
      }))
    };

    await fs.writeJson(path.join(outputDir, 'index.json'), index, { spaces: 2 });
  }

  /**
   * Generate Tailwind CSS migration instruction
   */
  private async generateTailwindMigration(config: MigrationTemplateConfig): Promise<ClaudeMigrationInstruction> {
    const prompt = await this.loadTemplate('tailwind-migration.md');

    return {
      id: `tailwind-migration-${config.toVersion}`,
      version: config.toVersion,
      title: 'Migrate to Tailwind CSS',
      description: 'Convert existing CSS and styling to Tailwind CSS utility classes',
      prompt: prompt.replace(/{{toVersion}}/g, config.toVersion),
      filePatterns: [
        '.*\\.(tsx?|jsx?)$',     // React components
        '.*\\.css$',             // CSS files
        '.*\\.module\\.css$',    // CSS modules
        'src/tokens/.*\\.(ts|js|json)$', // Design tokens
      ],
      dependencies: {
        add: ['tailwindcss', '@tailwindcss/forms', '@tailwindcss/typography'],
        remove: ['styled-components', '@emotion/react', '@emotion/styled'],
        update: []
      },
      postProcessing: {
        commands: [
          'npx tailwindcss init -p', // Initialize Tailwind config
        ],
        lintFix: true,
        formatCode: true
      },
      validationRules: [
        {
          name: 'no-inline-styles',
          pattern: /style\s*=\s*\{/,
          message: 'Inline styles should be converted to Tailwind classes',
          severity: 'warning'
        },
        {
          name: 'no-css-imports',
          pattern: /import\s+['"][^'"]*\.css['"]/,
          message: 'CSS imports should be removed in favor of Tailwind',
          severity: 'warning'
        }
      ]
    };
  }

  /**
   * Generate dependency migration instruction
   */
  private generateDependencyMigration(config: MigrationTemplateConfig): ClaudeMigrationInstruction {
    const { dependencies } = config.changes;
    
    return {
      id: `dependencies-migration-${config.toVersion}`,
      version: config.toVersion,
      title: 'Update Dependencies',
      description: 'Update package.json and related imports for new dependencies',
      prompt: `
# Dependency Migration for Dezzy v${config.toVersion}

Update the project dependencies and fix any import statements:

## Dependencies to Add:
${dependencies.add.map(dep => `- ${dep.name}@${dep.version}${dep.dev ? ' (dev)' : ''}`).join('\n')}

## Dependencies to Remove:
${dependencies.remove.map(dep => `- ${dep}`).join('\n')}

## Dependencies to Update:
${dependencies.update.map(dep => `- ${dep.name}: ${dep.from} → ${dep.to}`).join('\n')}

## Tasks:
1. Update package.json with new dependency versions
2. Find and update all import statements for changed packages
3. Remove imports for deprecated packages
4. Add any required setup code for new packages
5. Update any usage that may have changed between versions
      `.trim(),
      filePatterns: [
        'package\\.json$',
        '.*\\.(tsx?|jsx?)$',
        '.*\\.json$'
      ],
      dependencies: {
        add: dependencies.add.map(d => d.name),
        remove: dependencies.remove,
        update: dependencies.update
      }
    };
  }

  /**
   * Generate TypeScript migration instruction
   */
  private async generateTypeScriptMigration(config: MigrationTemplateConfig): Promise<ClaudeMigrationInstruction> {
    const prompt = await this.loadTemplate('typescript-migration.md');

    return {
      id: `typescript-migration-${config.toVersion}`,
      version: config.toVersion,
      title: 'Migrate to TypeScript',
      description: 'Convert JavaScript files to TypeScript and add type definitions',
      prompt: prompt.replace(/{{toVersion}}/g, config.toVersion),
      filePatterns: [
        '.*\\.(jsx?|tsx?)$',
        'tsconfig\\.json$',
        '.*\\.d\\.ts$'
      ],
      dependencies: {
        add: ['typescript', '@types/react', '@types/react-dom', '@types/node'],
        remove: [],
        update: []
      },
      postProcessing: {
        commands: ['npx tsc --noEmit'], // Type check
        lintFix: true,
        formatCode: true
      },
      validationRules: [
        {
          name: 'no-any-types',
          pattern: /:\s*any\b/,
          message: 'Avoid using "any" type, prefer specific types',
          severity: 'warning'
        }
      ]
    };
  }

  /**
   * Generate testing migration instruction
   */
  private async generateTestingMigration(config: MigrationTemplateConfig): Promise<ClaudeMigrationInstruction> {
    const testFramework = config.changes.newFeatures.testing;
    
    return {
      id: `testing-migration-${testFramework}-${config.toVersion}`,
      version: config.toVersion,
      title: `Migrate to ${testFramework}`,
      description: `Update test files and configuration to use ${testFramework}`,
      prompt: `
# Testing Framework Migration to ${testFramework}

Update all test files and testing configuration:

## Tasks:
1. Update test file imports and syntax for ${testFramework}
2. Migrate test utilities and setup files
3. Update test scripts in package.json
4. Convert any incompatible test patterns
5. Update configuration files (${testFramework}.config.js, etc.)
      `.trim(),
      filePatterns: [
        '.*\\.(test|spec)\\.(tsx?|jsx?)$',
        '__tests__/.*\\.(tsx?|jsx?)$',
        '.*\\.config\\.(js|ts)$',
        'package\\.json$'
      ],
      dependencies: {
        add: testFramework === 'vitest' 
          ? ['vitest', '@vitest/ui'] 
          : ['jest', '@testing-library/jest-dom'],
        remove: testFramework === 'vitest' ? ['jest'] : ['vitest'],
        update: []
      }
    };
  }

  /**
   * Generate code transform migration instruction
   */
  private async generateCodeTransformMigration(
    config: MigrationTemplateConfig,
    transform: VersionChangeSet['codeTransforms'][0]
  ): Promise<ClaudeMigrationInstruction> {
    return {
      id: `transform-${transform.name}-${config.toVersion}`,
      version: config.toVersion,
      title: transform.name,
      description: transform.description,
      prompt: `
# Code Transform: ${transform.name}

${transform.description}

## Complexity Level: ${transform.complexity.toUpperCase()}

Please apply this transformation systematically across all matching files, ensuring:
1. Functionality remains intact
2. Code style is consistent
3. Any related imports or dependencies are updated
4. Error handling is preserved
      `.trim(),
      filePatterns: transform.filePatterns
    };
  }

  /**
   * Create default migration templates
   */
  async createDefaultTemplates(): Promise<void> {
    await fs.ensureDir(this.templatesDir);

    // Tailwind migration template
    const tailwindTemplate = `
# Tailwind CSS Migration for Dezzy v{{toVersion}}

Convert all existing styling to Tailwind CSS utility classes while preserving the visual design.

## Key Tasks:

### 1. CSS to Tailwind Conversion
- Replace custom CSS classes with Tailwind utilities
- Convert flexbox layouts to Tailwind flex utilities
- Transform spacing (margin/padding) to Tailwind spacing scale
- Convert colors to Tailwind color palette or custom values
- Replace font styles with Tailwind typography utilities

### 2. Design Token Integration
- Map existing design tokens to Tailwind configuration
- Create custom Tailwind config for brand-specific values
- Update token references in components to use Tailwind classes

### 3. Component Refactoring
- Replace styled-components with Tailwind classes
- Convert CSS modules to Tailwind utilities
- Update className props to use Tailwind patterns
- Maintain responsive behavior with Tailwind breakpoints

### 4. Configuration Setup  
- Create tailwind.config.js with custom theme values
- Update PostCSS configuration if needed
- Remove unused CSS files and imports

## Guidelines:
- Use semantic class combinations (e.g., 'flex items-center justify-between')
- Preserve existing component APIs and props
- Maintain accessibility attributes and ARIA labels
- Keep responsive design intact with Tailwind breakpoints
- Extract commonly used class combinations into custom CSS classes if needed

## Output:
- Updated components with Tailwind classes
- New tailwind.config.js with project-specific theme
- Removed old CSS files and styling dependencies
- Updated imports throughout the codebase
    `.trim();

    await fs.writeFile(
      path.join(this.templatesDir, 'tailwind-migration.md'), 
      tailwindTemplate
    );

    // TypeScript migration template
    const typescriptTemplate = `
# TypeScript Migration for Dezzy v{{toVersion}}

Convert JavaScript files to TypeScript with proper type definitions.

## Key Tasks:

### 1. File Conversion
- Rename .js/.jsx files to .ts/.tsx
- Add proper TypeScript extensions to imports
- Update build configuration for TypeScript

### 2. Type Definitions  
- Add type annotations to function parameters and return values
- Define interfaces for props, state, and data structures
- Create type definitions for external APIs and data
- Import and use existing type definitions where available

### 3. Generic Type Usage
- Use React.FC for functional components or explicit typing
- Add generic types for hooks and utilities
- Define proper types for event handlers and callbacks

### 4. Configuration
- Create or update tsconfig.json with appropriate settings
- Update build scripts to handle TypeScript compilation
- Add type checking to CI/CD pipeline

## Guidelines:
- Start with permissive types and gradually make them more specific
- Use 'unknown' instead of 'any' when type is truly unknown  
- Leverage TypeScript's inference where possible
- Add JSDoc comments for complex type definitions
- Use strict mode configurations for better type safety

## Output:
- All JavaScript files converted to TypeScript
- Comprehensive type definitions for all components and utilities
- Updated build configuration and scripts
- Type-safe imports throughout the codebase
    `.trim();

    await fs.writeFile(
      path.join(this.templatesDir, 'typescript-migration.md'),
      typescriptTemplate
    );
  }

  /**
   * Helper methods
   */
  private async loadTemplate(filename: string): Promise<string> {
    const templatePath = path.join(this.templatesDir, filename);
    if (await fs.pathExists(templatePath)) {
      return fs.readFile(templatePath, 'utf8');
    }
    
    // Return default if template doesn't exist
    return `Migration template for ${filename} - customize this prompt for your specific needs.`;
  }

  private hasDependencyChanges(changes: VersionChangeSet): boolean {
    const { dependencies } = changes;
    return dependencies.add.length > 0 || 
           dependencies.remove.length > 0 || 
           dependencies.update.length > 0;
  }
}